// Device-local "contact aliases" (nicknames) for the brain (docs/08). The
// web/Android build keeps aliases in synchronous `localStorage`
// (storage/aliases.ts), which bare JSC does not provide — so the brain backs
// the same map with the native KV seam instead, under slot `merkury.aliases.v1`
// (value `{"<uuid>":"alias", ...}`). Aliases NEVER leave the device (docs/08
// §2): no server field, no API call, no log line, no push payload. The map is
// cached in memory and written through to KV so an edit takes effect without a
// round-trip (offline-first, prime directive).

import type { KvStore } from '../storage/kv.js';
import { sanitizeAlias } from '../storage/aliases.js';

const ALIASES_KEY = 'merkury.aliases.v1';

export class KvAliases {
  private cache: Record<string, string> | undefined;

  constructor(private readonly kv: KvStore) {}

  /** Hydrate the in-memory snapshot once from KV. */
  async load(): Promise<Readonly<Record<string, string>>> {
    if (this.cache === undefined) {
      const stored = await this.kv.get<Record<string, unknown>>(ALIASES_KEY).catch(() => undefined);
      const out: Record<string, string> = {};
      if (stored && typeof stored === 'object' && !Array.isArray(stored)) {
        for (const [k, v] of Object.entries(stored)) {
          if (typeof v === 'string' && v.length > 0) out[k] = v;
        }
      }
      this.cache = out;
    }
    return this.cache;
  }

  get(): Readonly<Record<string, string>> {
    return this.cache ?? {};
  }

  getAlias(userId: string): string | undefined {
    return this.get()[userId];
  }

  /**
   * Set (or clear) `userId`'s alias. The value is trimmed + sanitized (§3); a
   * null, empty, or all-whitespace value CLEARS it (deletes the entry). No-op
   * if the effective value is unchanged. Returns the stored alias, or null if
   * cleared.
   */
  async set(userId: string, alias: string | null): Promise<string | null> {
    await this.load();
    const current = this.cache ?? {};
    const clean = alias == null ? '' : sanitizeAlias(alias);
    if (clean === '') {
      if (!(userId in current)) return null; // already absent
      const next = { ...current };
      delete next[userId];
      this.cache = next;
      await this.kv.put(ALIASES_KEY, next);
      return null;
    }
    if (current[userId] === clean) return clean; // unchanged
    this.cache = { ...current, [userId]: clean };
    await this.kv.put(ALIASES_KEY, this.cache);
    return clean;
  }

  /** Delete a single user's alias (unfriend deletes the alias — §3). */
  async remove(userId: string): Promise<void> {
    await this.set(userId, null);
  }

  /**
   * Replace the WHOLE map (docs/08 §8.3: the alias cloud sync pulls a newer blob
   * and swaps the local map wholesale). Every entry is sanitized (§3) and
   * invalid/empty ones dropped, so a decrypted blob can never inject a malformed
   * alias. Writes through to KV.
   */
  async replaceAll(map: Record<string, string>): Promise<void> {
    const clean: Record<string, string> = {};
    for (const [userId, value] of Object.entries(map)) {
      if (typeof value !== 'string') continue;
      const alias = sanitizeAlias(value);
      if (alias.length > 0) clean[userId] = alias;
    }
    this.cache = clean;
    await this.kv.put(ALIASES_KEY, clean);
  }

  /** Wipe (sign-out must not leave the previous account's labels — §2). */
  async clear(): Promise<void> {
    this.cache = {};
    await this.kv.delete(ALIASES_KEY).catch(() => undefined);
  }
}
